import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import Modal from 'react-modal';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../../core/components/buttons/primary-button.component';
import notify from '../../../../core/components/notify/notify.component';
import StyleConstants from '../../../../core/styles.constants';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { useKeycloak } from '../../../authentication/keycloak/KeycloakProvider';
import { useEmployeePresenceDays } from '../hooks/dashboard.hook';
import { getEmployeePresenceCsvExport, type ReportingFilter } from '../services/dashboard.service';
import { type EmployeePresenceDay } from '../models/dashboard.models';
import * as styles from './employee-presence-detail.modal.styles';

interface Props {
  readonly officeUserId?: string;
  readonly fullName: string;
  readonly email: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly filter?: ReportingFilter;
  readonly handleClose: () => void;
}

/// <summary>Per-day reservation/check-in drill-down for one employee, over the period selected at
/// the top of the Reporting tab. The data can be copied as tab-separated text (straight into a
/// spreadsheet) or downloaded as the same CSV the panel exports, narrowed to this person.</summary>
const EmployeePresenceDetailModal = ({
  officeUserId,
  fullName,
  email,
  startDate,
  endDate,
  filter,
  handleClose,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const keycloak = useKeycloak();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const { initialized, detail, error } = useEmployeePresenceDays(officeUserId, startDate, endDate, filter);

  const yesLabel = t('reporting-employee-presence-yes').toString();
  const noLabel = t('reporting-employee-presence-no').toString();

  const days = useMemo(() => detail?.days ?? [], [detail]);

  const handleCopy = useCallback(async (): Promise<void> => {
    const header = [
      t('reporting-employee-presence-detail-date'),
      t('reporting-employee-presence-detail-reservation'),
      t('reporting-employee-presence-detail-checked-in'),
    ].join('\t');

    const rows = days.map((day) =>
      [
        day.date,
        day.hasReservation ? yesLabel : noLabel,
        day.isCheckedIn ? yesLabel : noLabel,
      ].join('\t'));

    try {
      await navigator.clipboard.writeText([header, ...rows].join('\n'));
      notify(t('reporting-employee-presence-copy-success'), true);
    } catch {
      // Clipboard access is denied outside a secure context and in some embedded browsers —
      // say so rather than silently doing nothing.
      notify(t('reporting-employee-presence-copy-error'));
    }
  }, [days, yesLabel, noLabel, t]);

  const handleCsvExport = useCallback(async (): Promise<void> => {
    if (!officeUserId) {
      return;
    }

    setIsExporting(true);
    const blob = await getEmployeePresenceCsvExport(startDate, endDate, keycloak, filter, officeUserId);
    setIsExporting(false);

    if (!blob) {
      notify(t('reporting-export-csv-error'));
      return;
    }

    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = `employee_presence_${email}_${startDate}_${endDate}.csv`;
    document.body.appendChild(element);
    element.click();
    URL.revokeObjectURL(element.href);
    element.remove();
    notify(t('reporting-export-csv-success'), true);
  }, [officeUserId, email, startDate, endDate, keycloak, filter, t]);

  return (
    <Modal
      ariaHideApp={false}
      isOpen={officeUserId !== undefined}
      onRequestClose={handleClose}
      style={styles.modalStyle(isMobile)}
    >
      <div className={style(styles.head)}>
        <div className={style(styles.headText)}>
          <h3 className={style(styles.name)}>{detail?.fullName ?? fullName}</h3>
          <div className={style(styles.email)}>{detail?.email ?? email}</div>
        </div>
        <div className={style(styles.headIcons)}>
          <button
            type="button"
            className={style(styles.iconButton)}
            aria-label={t('reporting-employee-presence-copy').toString()}
            title={t('reporting-employee-presence-copy').toString()}
            disabled={days.length === 0}
            onClick={handleCopy}
          >
            <ContentCopyRoundedIcon fontSize="small" />
          </button>
          <button
            type="button"
            className={style(styles.iconButton)}
            aria-label={t('close').toString()}
            title={t('close').toString()}
            onClick={handleClose}
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className={style(styles.body)}>
        {!initialized && (
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        )}

        {initialized && error && <span className={style(styles.message)}>{t('reporting-error-message')}</span>}

        {initialized && !error && days.length === 0 && (
          <span className={style(styles.message)}>{t('reporting-employee-presence-no-data')}</span>
        )}

        {initialized && !error && days.length > 0 && (
          <table className={style(styles.table)}>
            <thead>
              <tr>
                <th className={style(styles.headerCell)}>{t('reporting-employee-presence-detail-date')}</th>
                <th className={style(styles.headerCell)}>{t('reporting-employee-presence-detail-reservation')}</th>
                <th className={style(styles.headerCell)}>{t('reporting-employee-presence-detail-checked-in')}</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day: EmployeePresenceDay) => (
                <tr key={day.date}>
                  <td className={style(styles.cell)}>{day.date}</td>
                  <td className={style(styles.cell, day.hasReservation ? styles.yes : styles.no)}>
                    {day.hasReservation ? yesLabel : noLabel}
                  </td>
                  <td className={style(styles.cell, day.isCheckedIn ? styles.yes : styles.no)}>
                    {day.isCheckedIn ? yesLabel : noLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={style(styles.footer)}>
        <PrimaryButton onClick={handleCsvExport} loading={isExporting} disabled={days.length === 0}>
          {t('reporting-export-csv')}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default EmployeePresenceDetailModal;
